<script setup lang="ts">
import {useApi} from '@/ts/composables/stateful/use-api';
import {MULTI_LANGUAGE} from '@/ts/utils/consts';
import {useQuasar} from 'quasar';
import BalmBtn from '@/vue/atoms/balm-btn.vue';
import {ref, computed, onMounted, watch} from 'vue';
import {useLoadingNotification} from '@/ts/composables/pure/use-loading-notification';
import {useI18n} from 'vue-i18n';
import {useUserStore} from '@/ts/store/user-store';
import {ColorScheme, MinFontSize, useUserPreferencesStore} from '@/ts/store/user-preferences.store';
import {useLanguageChange} from '@/ts/composables/stateful/use-language-change';
import {Language} from '@/ts/types/component/localization.type';
import {TOUR_CONFIG, TOUR_NAMES} from '@/ts/utils/tour-constants';
import {useTourStore} from '@/ts/store/tour-store';

const api = useApi();
const {t: $t} = useI18n();
const $q = useQuasar();
const userStore = useUserStore();
const userPreferencesStore = useUserPreferencesStore();
const {languageOptions, getInitialLanguage, applyLanguageChange} = useLanguageChange();
const tourStore = useTourStore();

// Form fields - local state
const selectedTheme = ref<ColorScheme>(ColorScheme.system);
const selectedFontSize = ref<number>(MinFontSize.normal);
const selectedLanguage = ref<Language>(Language.English);
const showProductTour = ref<boolean>(false);

// Original values (to detect changes)
const originalTheme = ref<ColorScheme>(ColorScheme.system);
const originalFontSize = ref<number>(MinFontSize.normal);
const originalLanguage = ref<Language>(Language.English);
const originalShowTour = ref<boolean>(false);

// Initialize form with current values
const initializeForm = (): void => {
  // Theme
  selectedTheme.value = userPreferencesStore.colorScheme;
  originalTheme.value = userPreferencesStore.colorScheme;

  // Font size
  const fontSize = userStore.userData?.minFontSize ?? MinFontSize.normal;
  selectedFontSize.value = fontSize;
  originalFontSize.value = fontSize;

  // Language - ensure it's a supported language, fallback to English if not
  const currentLang = getInitialLanguage();
  selectedLanguage.value = currentLang;
  originalLanguage.value = currentLang;

  // Tour - ON if tour should show (version outdated or null/0)
  const shouldShow = tourStore.shouldShowTour(TOUR_NAMES.onboardingTour);
  showProductTour.value = shouldShow;
  originalShowTour.value = shouldShow;
};

onMounted(initializeForm);

// Watch for external theme changes (e.g., from overflow menu)
watch(() => userPreferencesStore.colorScheme, (newTheme) => {
  selectedTheme.value = newTheme;
  originalTheme.value = newTheme;
});

// Theme options
const themeOptions = computed(() => [
  {label: $t('system_theme'), value: ColorScheme.system},
  {label: $t('light_theme'), value: ColorScheme.light},
  {label: $t('dark_theme'), value: ColorScheme.dark},
]);

// Font size options
const fontSizeOptions = computed(() => [
  {label: $t('font_size_normal'), value: MinFontSize.normal},
  {label: $t('font_size_large'), value: MinFontSize.large},
  {label: $t('font_size_extra_large'), value: MinFontSize.extraLarge},
]);

// Check if form has changes
const hasChanges = computed(() => {
  return (
    selectedTheme.value !== originalTheme.value ||
    selectedFontSize.value !== originalFontSize.value ||
    selectedLanguage.value !== originalLanguage.value ||
    showProductTour.value !== originalShowTour.value
  );
});

// Build update payload based on changes
const buildUpdatePayload = (): {
  colorMode?: string;
  minFontSize?: number;
  languageCode?: string;
  onboardingTourVersion?: number;
} => {
  const payload: {
    colorMode?: string;
    minFontSize?: number;
    languageCode?: string;
    onboardingTourVersion?: number;
  } = {};

  if (selectedTheme.value !== originalTheme.value) {
    payload.colorMode = selectedTheme.value;
  }
  if (selectedFontSize.value !== originalFontSize.value) {
    payload.minFontSize = selectedFontSize.value;
  }
  if (selectedLanguage.value !== originalLanguage.value) {
    payload.languageCode = selectedLanguage.value;
  }
  if (showProductTour.value !== originalShowTour.value) {
    payload.onboardingTourVersion = showProductTour.value ? 0 : TOUR_CONFIG.onboardingTour.version;
    tourStore.setTourSeen(TOUR_NAMES.onboardingTour, !showProductTour.value);
  }

  return payload;
};

// Update local stores after successful API call
const updateLocalStores = (payload: ReturnType<typeof buildUpdatePayload>): void => {
  if (payload.colorMode) {
    userPreferencesStore.colorScheme = selectedTheme.value;
  }
  if ('minFontSize' in payload) {
    userPreferencesStore.minFontSize = selectedFontSize.value;
    if (userStore.userData) {
      userStore.userData.minFontSize = selectedFontSize.value;
    }
  }
  if ('onboardingTourVersion' in payload && userStore.userData) {
    userStore.userData.onboardingTourVersion = payload.onboardingTourVersion;
  }
};

// Sync original values with current values after save
const syncOriginalValues = (languageChanged: boolean): void => {
  originalTheme.value = selectedTheme.value;
  originalFontSize.value = selectedFontSize.value;
  originalShowTour.value = showProductTour.value;
  if (languageChanged) {
    originalLanguage.value = selectedLanguage.value;
  }
};

// Save all preferences
const [savePreferences, loading] = useLoadingNotification(
  async() => {
    const languageChanged = selectedLanguage.value !== originalLanguage.value;
    const updatePayload = buildUpdatePayload();

    await api.me.updateMe(updatePayload);

    updateLocalStores(updatePayload);
    syncOriginalValues(languageChanged);

    if (languageChanged) {
      await applyLanguageChange(selectedLanguage.value);
    }
  },
  () => {
    $q.notify($t('preferences_updated'));
  },
);
</script>

<template>
  <div class="account-tab-page">
    <h3 class="info-title">
      {{ $t('preferences') }}
    </h3>

    <div class="preferences-content">
      <!-- Left Column -->
      <div class="preferences-column">
        <!-- Theme Section -->
        <section class="preferences-section">
          <h4 class="section-title">
            {{ $t('theme') }}
          </h4>
          <div class="card">
            <span class="card-label">{{ $t('colors') }}</span>
            <q-select
              v-model="selectedTheme"
              :options="themeOptions"
              :label="$t('theme')"
              emit-value
              map-options
            />
          </div>
        </section>

        <!-- Accessibility Section -->
        <section class="preferences-section">
          <h4 class="section-title">
            {{ $t('accessibility') }}
          </h4>
          <div class="card">
            <span class="card-label">{{ $t('font_size') }}</span>
            <q-select
              v-model="selectedFontSize"
              :options="fontSizeOptions"
              :label="$t('size')"
              emit-value
              map-options
            />
          </div>
        </section>
      </div>

      <!-- Right Column -->
      <div class="preferences-column">
        <!-- General Section: only where the deployment offers more than one language, like the
             language switcher on the public pages and the admin's profile. -->
        <section v-if="MULTI_LANGUAGE" class="preferences-section">
          <h4 class="section-title">
            {{ $t('general') }}
          </h4>
          <div class="card">
            <span class="card-label">{{ $t('language') }}</span>
            <q-select
              v-model="selectedLanguage"
              :options="languageOptions"
              :label="$t('language')"
              emit-value
              map-options
            />
          </div>
        </section>

        <!-- Product Tour Section -->
        <section class="preferences-section">
          <h4 class="section-title">
            {{ $t('product_tour') }}
          </h4>
          <div class="card toggle-card">
            <span class="description">{{ $t('show_product_tour_description') }}</span>
            <q-toggle
              v-model="showProductTour"
              :aria-label="$t('product_tour')"
              color="positive"
              size="md"
            />
          </div>
        </section>
      </div>
    </div>

    <!-- Save Button -->
    <div class="form-actions">
      <balm-btn
        :label="$t('save_changes')"
        theme="green"
        paddings="p2-5"
        :loading="loading"
        :disable="!hasChanges"
        class="save-btn"
        @click="savePreferences"
      />
    </div>
  </div>
</template>

<style scoped lang="scss">

.account-tab-page {
  @include q-card-white;
  padding: var(--Spacing-spacing-5);
}

.info-title {
  @include font-display-3;
  color: var(--Neutral-UI-neutral-ui-100);
  margin-bottom: var(--Spacing-spacing-5);
}

.preferences-content {
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-6);
  margin-top: var(--Spacing-spacing-5);

  @include screen-md {
    flex-direction: row;
    gap: var(--Spacing-spacing-6);
  }
}

.preferences-column {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: var(--Spacing-spacing-5);
}

.preferences-section {
  .section-title {
    @include font-h3;
    margin-bottom: var(--Spacing-spacing-3);
  }
}

.card {
  @include q-card-10;
  padding: var(--Spacing-spacing-4);

  .card-label {
    @include font-body-bold;
    display: block;
    margin-bottom: var(--Spacing-spacing-2);
  }
}

.toggle-card {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: var(--Spacing-spacing-4);

  .description {
    @include font-body;
    color: var(--Neutral-UI-neutral-ui-100);
  }
}

.form-actions {
  margin-top: var(--Spacing-spacing-6);
  display: flex;
  justify-content: flex-start;

  .save-btn {
    min-width: 140px;
  }
}

</style>
